/**
 * Leaguemate Scouting — S3.5 archetype crown (pure, no I/O).
 *
 * All six axes now exist: reachability, FAAB, trade-activity, initiation,
 * win-now/rebuild, positional. We crown a NAMED archetype only when the
 * relevant axes align AND the sample supports it (§7b discipline; Mock1 ethos:
 * don't force buckets). Otherwise no label — the UI shows the raw dimensions.
 *
 * Crowns are evaluated in priority order; the first whose evidence holds wins.
 * Thin-sample or conflicting managers get { archetype: null, reason }.
 */

import { TRADE_CONFIDENCE_MIN, AFFINITY_MIN_FRANCHISES } from "./scoutConfig.js";

const crown = (archetype, basis, evidence) => ({ archetype, basis, evidence });

/**
 * @param dims { tradeStyle, faab, reachability, rebuild, positional, affinityMeta }
 */
export function crownArchetype(dims = {}) {
  const { tradeStyle = {}, faab = {}, reachability = {}, rebuild = {}, affinityMeta = {} } = dims;
  const usable = affinityMeta.usableFranchiseCount ?? 0;
  const trades = tradeStyle.tradeCount ?? 0;
  const thin = affinityMeta.limitedData || usable < AFFINITY_MIN_FRANCHISES;

  // Limited data → NO crown, full stop (spec honesty state). A single-franchise
  // read isn't enough to crown an archetype even if FAAB/trades look extreme;
  // render the raw axes instead. (TRADE_CONFIDENCE_MIN referenced below.)
  if (thin) {
    return { archetype: null, reason: "limited data — not enough cross-league signal", confidence: "none" };
  }

  // 1. Set-and-Forget / Dormant (gating) — dormant + light trading.
  if (reachability.band === "dormant" && tradeStyle.activityBand === "low") {
    return crown("Set-and-Forget / Dormant", "dormant + light trading", {
      reachability: reachability.band, activity: tradeStyle.activityBand, faab: faab.band,
    });
  }

  // 2. Wheeler-Dealer — high activity + proactive initiation + currently active
  //    (requires a real trade sample for the initiation read).
  if (
    !thin && trades >= TRADE_CONFIDENCE_MIN &&
    (tradeStyle.activityBand === "high" || tradeStyle.activityBand === "hyperactive") &&
    tradeStyle.initiationBand === "proactive" &&
    reachability.band === "active"
  ) {
    return crown("Wheeler-Dealer", "high trade activity + initiates + active", {
      activity: tradeStyle.activityBand, initiation: tradeStyle.initiationRate, reachability: reachability.band,
    });
  }

  // 3. Patient Rebuilder — confident rebuild read (intent + state agree).
  if (rebuild.label === "rebuild" && rebuild.confidence === "high") {
    return crown("Patient Rebuilder", "acquiring future picks + young roster", {
      netFuturePicks: rebuild.netFuturePicks, youthTilt: rebuild.youthTilt,
    });
  }

  // 4. Win-Now Contender — confident win-now read (intent + state agree).
  if (rebuild.label === "win-now" && rebuild.confidence === "high") {
    return crown("Win-Now Contender", "spending future picks + veteran roster", {
      netFuturePicks: rebuild.netFuturePicks, youthTilt: rebuild.youthTilt,
    });
  }

  // 5. Aggressive Spender — FAAB-aggressive (lower priority: many archetypes
  //    also spend, so only crown it when nothing more specific fired).
  if (faab.band === "aggressive") {
    return crown("Aggressive Spender", "high FAAB spend", { faabPct: faab.spentPct });
  }

  // No axis dominates → no crown (show raw dimensions).
  return {
    archetype: null,
    reason: thin ? "thin sample — limited cross-league data" : "mixed — no axis dominates",
    confidence: "none",
  };
}
